<!-- src/views/pages/finance-accounts/sales/invoices-list.vue -->
<script setup>
import { ref, computed, onMounted } from "vue";
import { useInvoiceStore } from "@/stores/invoiceStore";
import invoicesTable from "./invoices-table.vue";
import { useRoute } from "vue-router";
import Modal from "bootstrap/js/dist/modal";
import * as Toast from "@/utils/toast";

const title = ref("Invoices");
const text = ref("Finance & Accounts");
const text1 = ref("Invoices");

const startdate = ref(new Date());
const startdateOne = ref(new Date());
const dateFormat = ref("dd-MM-yyyy");

const invoiceStore = useInvoiceStore();
const loading = computed(() => invoiceStore.loading);
const stats = invoiceStore.stats;
const meta = computed(() => invoiceStore.meta);

const statusFilter = ref("");
const complianceFilter = ref("");
const documentTypeFilter = ref("");
const sortBy = ref("last7"); // we just keep for UI now
const deletingId = ref(null);

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const paymentInvoice = ref(null);
const paymentSaving = ref(false);
const paymentAmount = ref(null);
const paymentDate = ref(dateToYmd(new Date()));
const paymentMethod = ref("Cash");
const paymentReference = ref("");
const clientId = ref(null);
const paymentNotes = ref("");

const paymentMethods = ref([
  "Cash",
  "Credit Card",
  "Debit Card",
  "Bank Transfer",
  "Mobile Money",
]);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function dateToYmd(d) {
  if (!d) return null;
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function applyFilters(page = 1) {
  invoiceStore.filters.status = statusFilter.value || "";
  invoiceStore.filters.compliance_status = complianceFilter.value || "";
  invoiceStore.filters.document_type = documentTypeFilter.value || "";
  invoiceStore.filters.from = dateToYmd(startdate.value);
  invoiceStore.filters.to = dateToYmd(startdateOne.value);
  await invoiceStore.fetchAll({ page });
  await invoiceStore.fetchStats();
}

function handleStatusSelect(status) {
  statusFilter.value = status;
  applyFilters(1);
}

function handleComplianceSelect(status) {
  complianceFilter.value = status;
  applyFilters(1);
}

function handleDocumentTypeSelect(type) {
  documentTypeFilter.value = type;
  applyFilters(1);
}

const statusFilterLabel = computed(() => {
  if (!statusFilter.value) return "Select Status";
  return statusFilter.value.charAt(0).toUpperCase() + statusFilter.value.slice(1);
});

const complianceFilterLabel = computed(() => {
  if (!complianceFilter.value) return "Compliance Status";
  if (complianceFilter.value === "not_submitted") return "Not Submitted";
  return complianceFilter.value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
});

const documentTypeFilterLabel = computed(() => {
  if (!documentTypeFilter.value) return "Document Type";
  if (documentTypeFilter.value === "invoice") return "Invoices";
  if (documentTypeFilter.value === "credit_note") return "Credit Notes";
  if (documentTypeFilter.value === "debit_note") return "Debit Notes";
  return "Document Type";
});

const documentContext = computed(() => {
  if (documentTypeFilter.value === "credit_note") {
    return {
      singular: "Credit Note",
      plural: "Credit Notes",
      export: "credit-notes-report",
    };
  }

  if (documentTypeFilter.value === "debit_note") {
    return {
      singular: "Debit Note",
      plural: "Debit Notes",
      export: "debit-notes-report",
    };
  }

  return {
    singular: "Invoice",
    plural: "Invoices",
    export: "invoice-report",
  };
});

const breadcrumbTitle = computed(() => documentContext.value.plural);
const breadcrumbText1 = computed(() => documentContext.value.plural);
const countLabel = computed(() => `${meta.value.total || 0} ${documentContext.value.plural}`);
const totalValueLabel = computed(() => `Total ${documentContext.value.singular} Value`);
const draftValueLabel = computed(() => `Draft ${documentContext.value.singular} Value`);
const secondaryValueLabel = computed(() =>
  documentTypeFilter.value === "invoice" || !documentTypeFilter.value
    ? "Outstanding"
    : "Open Balance"
);
const fourthValueLabel = computed(() =>
  documentTypeFilter.value === "invoice" || !documentTypeFilter.value
    ? "Collected Amount"
    : "Applied Amount"
);
const addActionLabel = computed(() => {
  if (documentTypeFilter.value === "credit_note") return "Add Credit Note";
  if (documentTypeFilter.value === "debit_note") return "Add Debit Note";
  return "Add Invoice";
});
const revenuePct = computed(() => {
  const total = Math.abs(Number(stats.total || 0));
  const revenue = Math.abs(Number(stats.revenue || 0));
  if (!total) return 0;
  return Math.min((revenue / total) * 100, 100);
});

function handlePageChange(page) {
  applyFilters(page);
}

function showDeleteInvoice() {
  const el = document.getElementById("delete_invoice");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.show();
}

function handleDeleteRequest(id) {
  deletingId.value = id;
  showDeleteInvoice();
}

function hideDelete() {
  const el = document.getElementById("delete_invoice");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.hide();
}

function showReceivePayment() {
  const el = document.getElementById("receive_payment_modal");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.show();
}

function hidePaymentModal() {
  const el = document.getElementById("receive_payment_modal");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.hide();
}

//payments

function openPaymentModal(invoice) {
  paymentInvoice.value = invoice;
  paymentAmount.value = Number(invoice.amount_due || 0) || null; // default outstanding
  paymentDate.value = dateToYmd(new Date());
  paymentMethod.value = "Cash";
  paymentReference.value = "";
  paymentNotes.value = "";
  clientId.value = invoice.client_id;

  showReceivePayment();
}

async function submitPayment() {
  if (!paymentInvoice.value) return;

  const outstanding = Number(paymentInvoice.value.amount_due || 0);
  const amt = Number(paymentAmount.value || 0);

  if (!amt || amt <= 0) {
    notifyError("Amount must be greater than zero");
    return;
  }
  if (amt > outstanding) {
    notifyError("Amount exceeds outstanding balance");
    return;
  }

  paymentSaving.value = true;
  try {
    await invoiceStore.receivePayment(paymentInvoice.value.id, {
      amount: amt,
      payment_date: paymentDate.value,
      client_id: clientId.value || paymentInvoice.value.client_id,
      method: paymentMethod.value,
      reference: paymentReference.value,
      notes: paymentNotes.value,
    });

    // refresh current page list + stats
    await applyFilters(meta.value.current_page || 1);

    hidePaymentModal();

    paymentInvoice.value = null;
  } catch (e) {
    notifyError(
      e?.response?.data?.message || e.message || "Failed to record payment"
    );
  } finally {
    paymentSaving.value = false;
  }
}

async function confirmDelete() {
  if (!deletingId.value) return;
  try {
    await invoiceStore.destroy(deletingId.value);
  } finally {
    deletingId.value = null;
    hideDelete();
  }
}


async function exportPdf() {
      try {
        const blob = await invoiceStore.exportPdf();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentContext.value.export}-${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        // error already handled by store toast
        console.error("Export invoice report failed", e);
      }
    }

    async function exportExcel() {
      try {
        const blob = await invoiceStore.exportExcel();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentContext.value.export}-${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Export invoice report Excel failed", e);
      }
    }

onMounted(async () => {
  await invoiceStore.fetchAll();
  await invoiceStore.fetchStats();
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="breadcrumbTitle" :text="text" :text1="breadcrumbText1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="me-2 mb-2">
            <div class="dropdown" v-can="'Invoice.Export'">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-2"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a href="javascript:void(0);" class="dropdown-item rounded-1" @click.prevent="exportPdf">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0);" class="dropdown-item rounded-1" @click.prevent="exportExcel">
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-2">
            <router-link
              to="/sales/invoices/add-invoices"
              class="btn btn-primary d-flex align-items-center"
              v-can="'Invoice.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>{{ addActionLabel }}
            </router-link>
          </div>
          <div class="ms-2 head-icons">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Invoice Data Cards -->
      <div class="row dq-stat-grid">
        <div class="col-xl-3 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center overflow-hidden mb-2">
                <div>
                  <p class="fs-12 fw-normal mb-1 text-truncate">
                    {{ totalValueLabel }}
                  </p>
                  <h5>{{ formatMoney(stats.total) }}</h5>
                </div>
              </div>
              <div class="attendance-report-bar mb-2">
                <div
                  class="progress"
                  role="progressbar"
                  aria-label="Total invoice"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  style="height: 5px"
                >
                  <div
                    class="progress-bar bg-pink"
                    :style="{ width: (stats.total_pct || 0) + '%' }"
                  ></div>
                </div>
              </div>
              <!-- <div>
                <p class="fs-12 fw-normal d-flex align-items-center text-truncate">
                  <span class="text-success fs-12 d-flex align-items-center me-1">
                    <i class="ti ti-arrow-wave-right-up me-1"></i> {{ (stats.total_pct || 0).toFixed(0) }}%
                  </span>
                  of max bucket
                </p>
              </div> -->
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center overflow-hidden mb-2">
                <div>
                  <p class="fs-12 fw-normal mb-1 text-truncate">{{ secondaryValueLabel }}</p>
                  <h5>{{ formatMoney(stats.outstanding) }}</h5>
                </div>
              </div>
              <div class="attendance-report-bar mb-2">
                <div class="progress" role="progressbar" style="height: 5px">
                  <div
                    class="progress-bar bg-purple"
                    :style="{ width: (stats.outstanding_pct || 0) + '%' }"
                  ></div>
                </div>
              </div>
              <!-- <div>
                <p class="fs-12 fw-normal d-flex align-items-center text-truncate">
                  <span class="text-danger fs-12 d-flex align-items-center me-1">
                    <i class="ti ti-arrow-wave-right-up me-1"></i>
                    {{ (stats.outstanding_pct || 0).toFixed(0) }}%
                  </span>
                  of max bucket
                </p>
              </div> -->
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center overflow-hidden mb-2">
                <div>
                  <p class="fs-12 fw-normal mb-1 text-truncate">{{ draftValueLabel }}</p>
                  <h5>{{ formatMoney(stats.draft) }}</h5>
                </div>
              </div>
              <div class="attendance-report-bar mb-2">
                <div class="progress" role="progressbar" style="height: 5px">
                  <div
                    class="progress-bar bg-warning"
                    :style="{ width: (stats.draft_pct || 0) + '%' }"
                  ></div>
                </div>
              </div>
              <!-- <div>
                <p class="fs-12 fw-normal d-flex align-items-center text-truncate">
                  <span class="text-success fs-12 d-flex align-items-center me-1">
                    <i class="ti ti-arrow-wave-right-up me-1"></i>
                    {{ (stats.draft_pct || 0).toFixed(0) }}%
                  </span>
                  of max bucket
                </p>
              </div> -->
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center overflow-hidden mb-2">
                <div>
                  <p class="fs-12 fw-normal mb-1 text-truncate">
                    {{ fourthValueLabel }}
                  </p>
                  <h5>{{ formatMoney(stats.revenue || 0) }}</h5>
                </div>
              </div>
              <div class="attendance-report-bar mb-2">
                <div class="progress" role="progressbar" style="height: 5px">
                  <div
                    class="progress-bar bg-danger"
                    :style="{ width: revenuePct + '%' }"
                  ></div>
                </div>
              </div>
              <!-- <div>
                <p class="fs-12 fw-normal d-flex align-items-center text-truncate">
                  <span class="text-danger fs-12 d-flex align-items-center me-1">
                    <i class="ti ti-arrow-wave-right-up me-1"></i>
                    {{ (stats.overdue_pct || 0).toFixed(0) }}%
                  </span>
                  of max bucket
                </p>
              </div> -->
            </div>
          </div>
        </div>
      </div>
      <!-- /Invoice Data -->

      <div class="row dq-stat-grid">
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <p class="fs-12 fw-normal mb-1 text-truncate">eTIMS Accepted</p>
              <h5>{{ stats.etims_accepted || 0 }}</h5>
            </div>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <p class="fs-12 fw-normal mb-1 text-truncate">Queued</p>
              <h5>{{ stats.etims_queued || 0 }}</h5>
            </div>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <p class="fs-12 fw-normal mb-1 text-truncate">Rejected</p>
              <h5>{{ stats.etims_rejected || 0 }}</h5>
            </div>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <p class="fs-12 fw-normal mb-1 text-truncate">Failed</p>
              <h5>{{ stats.etims_failed || 0 }}</h5>
            </div>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <p class="fs-12 fw-normal mb-1 text-truncate">Not Submitted</p>
              <h5>{{ stats.etims_not_submitted || 0 }}</h5>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoice DataTable -->
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
            >
              <h5 class="d-flex align-items-center">
                {{ documentContext.plural }}
                <span class="badge badge-dark-transparent ms-2">
                  {{ countLabel }}
                </span>
              </h5>
              <div class="d-flex align-items-center flex-wrap row-gap-3 dq-list-toolbar">
                <div class="input-icon position-relative w-120 me-2">
                  <span class="input-icon-addon">
                    <i class="ti ti-calendar"></i>
                  </span>
                  <date-picker
                    v-model="startdate"
                    class="form-control datetimepicker"
                    placeholder="Created Date"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                    @change="applyFilters(1)"
                  />
                </div>
                <div class="input-icon position-relative w-120 me-2">
                  <span class="input-icon-addon">
                    <i class="ti ti-calendar"></i>
                  </span>
                  <date-picker
                    v-model="startdateOne"
                    class="form-control datetimepicker"
                    placeholder="Due Date"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                    @change="applyFilters(1)"
                  />
                </div>
                <div class="dropdown me-2">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ statusFilterLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleStatusSelect('')"
                        >All Statuses</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleStatusSelect('paid')"
                        >Paid</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleStatusSelect('overdue')"
                        >Overdue</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleStatusSelect('pending')"
                        >Pending</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleStatusSelect('draft')"
                        >Draft</a
                      >
                    </li>
                  </ul>
                </div>
                <div class="dropdown me-2">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ documentTypeFilterLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleDocumentTypeSelect('')"
                        >All Documents</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleDocumentTypeSelect('invoice')"
                        >Invoices</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleDocumentTypeSelect('credit_note')"
                        >Credit Notes</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleDocumentTypeSelect('debit_note')"
                        >Debit Notes</a
                      >
                    </li>
                  </ul>
                </div>
                <div class="dropdown me-2">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ complianceFilterLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleComplianceSelect('')"
                        >All Compliance States</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleComplianceSelect('accepted')"
                        >Accepted</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleComplianceSelect('queued')"
                        >Queued</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleComplianceSelect('rejected')"
                        >Rejected</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleComplianceSelect('failed')"
                        >Failed</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="handleComplianceSelect('not_submitted')"
                        >Not Submitted</a
                      >
                    </li>
                  </ul>
                </div>
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-white d-inline-flex align-items-center fs-12"
                    data-bs-toggle="dropdown"
                  >
                    <span class="fs-12 d-inline-flex me-1">Sort By : </span>
                    Last 7 Days
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="
                          sortBy = 'last7';
                          applyFilters(1);
                        "
                        >Last 7 Days</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="
                          sortBy = 'created';
                          applyFilters(1);
                        "
                        >Created Date</a
                      >
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="
                          sortBy = 'due';
                          applyFilters(1);
                        "
                        >Due Date</a
                      >
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="custom-datatable-filter table-responsive">
                <invoices-table
                  :loading="loading"
                  :meta="meta"
                  @page-change="handlePageChange"
                  @delete="handleDeleteRequest"
                  @receive-payment="openPaymentModal"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Invoice DataTable -->
    </div>

    <!-- Footer -->
    <div
      class="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
    <!-- /Footer -->

    <!-- Delete Modal -->
    <div class="modal fade" id="delete_invoice" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center">
            <span
              class="avatar avatar-xl bg-transparent-danger text-danger mb-3"
            >
              <i class="ti ti-trash-x fs-36"></i>
            </span>
            <h4 class="mb-1">Confirm Delete</h4>
            <p class="mb-3">
              You want to delete the selected invoice. This can't be undone once
              you delete.
            </p>
            <div class="d-flex justify-content-center">
              <a
                href="javascript:void(0);"
                class="btn btn-light me-3"
                data-bs-dismiss="modal"
                >Cancel</a
              >
              <button
                type="button"
                class="btn btn-danger"
                @click="confirmDelete"
              >
                <span
                  v-if="invoiceStore.loadingFordelete"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /Delete Modal -->

    <!--  Receive payment Modal -->
    <!-- Receive Payment Modal -->
    <div class="modal fade" id="receive_payment_modal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Receive Payment
              <span v-if="paymentInvoice" class="text-muted fs-12 ms-2">
                ({{ paymentInvoice.invoice_no }} –
                {{ paymentInvoice.client?.name || "Client" }})
              </span>
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="paymentInvoice">
              <p class="text-muted mb-2">
                Outstanding:
                <span class="fw-semibold text-dark">
                  {{ formatMoney(paymentInvoice.amount_due) }}
                </span>
              </p>

              <div class="mb-3">
                <label class="form-label">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  v-model.number="paymentAmount"
                  class="form-control"
                />
              </div>

              <div class="mb-3">
                <label class="form-label">Payment Date</label>
                <input type="date" class="form-control" v-model="paymentDate" />
              </div>

              <div class="mb-3">
                <label class="form-label">Method</label>
                <select class="form-select" v-model="paymentMethod">
                  <option v-for="m in paymentMethods" :key="m" :value="m">
                    {{ m }}
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label">Reference</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="paymentReference"
                  placeholder="Receipt / transaction ref"
                />
              </div>

              <div class="mb-0">
                <label class="form-label">Notes</label>
                <textarea
                  class="form-control"
                  rows="2"
                  v-model="paymentNotes"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              data-bs-dismiss="modal"
              :disabled="paymentSaving"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="paymentSaving"
              @click="submitPayment"
            >
              <span v-if="!paymentSaving">Save Payment</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!--Receive payment modal  -->
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .w-120 {
    width: 100% !important;
  }

  .input-icon.position-relative {
    width: 100%;
  }

  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .btn,
  .dq-list-toolbar .form-control,
  .dq-list-toolbar :deep(.mx-datepicker) {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


